import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import HomeIcon from '../../../assets/svg/HomeICon';
import RewardIcon from '../../../assets/svg/RewardIcon';
import MoreIcon from '../../../assets/svg/MoreIcon';
import OfferIcon from '../../../assets/svg/OfferIcon';
import ManagedCard from '../../../assets/svg/ManagedCard';
import { DownArrow } from '../../../assets/svg/ArrowIcon';
import {
  LanguageIcon,
  UserICon,
  CloseIcon,
} from '../../../assets/svg/MenuIcons';
import {
  RespViewEPPIcon,
  RespViewServiceReqIcon,
  RespSendAndViewRequestIcon,
} from '../../../assets/svg/ViewEPPIcon';
import { RespManageAlertsIcon } from '../../../assets/svg/ManageAlertsIcon';
import { RespManagePasskeyIcon } from '../../../assets/svg/ManagePasskeyIcon';
import { RespContactUsIcon } from '../../../assets/svg/ContactUsIcon';
import { RespPreauthorizedDebitIcon } from '../../../assets/svg/PreauthorizedDebitIcon';
import {
  RespCardFeaturesIcon,
  RespUnlinkRogersAcc,
} from '../../../assets/svg/CardFeaturesIcon';
import {
  RespFAQsIcon,
  RespLegalPrivacyIcon,
} from '../../../assets/svg/FAQsIcon';
import { EventType, useAnalytics } from '../../Analytics';
import FAQsModal from '../../../components/FAQsModal';
import LegalPrivacyModal from '../../../components/LegalPrivacyModal';
import Modal from 'react-modal';
import ContactUs from '../../../screens/ContactUs';
import { logBrazeCustomEvent } from '../../../config/braze';
import CommanModal from '../../../components/CommanModal';
import {
  PasskeyFeatureFlagInput,
  resolvePasskeyFeatureFlags,
} from '@rb-serve-app/core';

type MobileSideMenuContentfulData = {
  rb_header?: {
    signOutCtaTitle?: string;
    myAccountTitle?: string;
    englishTitle?: string;
    frenchTitle?: string;
  };
  rb_navigation?: {
    homeLogoTitle?: string;
    offersLogoTitle?: string;
    manageCardLogoTitle?: string;
    rewardsLogoTitle?: string;
    moreLogoTitle?: string;
    rbNotifications?: {
      enable_notification_popup_title?: string;
      enable_notification_popup_primary_button?: string;
    };
    moreMenu?: {
      view_epp?: string;
      manage_alerts?: string;
      enable_push_notifications?: string;
      contact_us?: string;
      pre_autorised_debit?: string;
      card_feature?: string;
      send_and_view_msgs?: string;
      faqs?: string;
      legal_privacy?: string;
      view_service_req?: string;
      unLink_rogers_acc?: string;
      my_profile?: string;
      manage_passkey?: string;
      [key: string]: string | undefined;
    };
  };
};

type MobileSideMenuFeatureFlagData = {
  passkey?: PasskeyFeatureFlagInput;
  navigation?: {
    offers?: boolean;
    offersFeatureFlaggedBasedOnProductType?: Record<string, boolean>;
    cardFeature?: boolean;
  };
  more?: {
    manage_alerts?: boolean;
    contact_us?: boolean;
    pre_authorized_debit?: boolean;
    card_features?: boolean;
    send_and_view_msgs?: boolean;
    faq?: boolean;
    view_service_request?: boolean;
    manage_passkey?: boolean;
    [key: string]: boolean | undefined;
  };
  webMore?: {
    legal_privacy?: boolean;
    enable_push_notifications?: boolean;
    notifications?: boolean;
  };
};

type Props = {
  contentfulData: MobileSideMenuContentfulData;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  isMenuOpenValue: boolean;
  featureFlaggingData: MobileSideMenuFeatureFlagData;
  setShowFeatureFlagMessage: React.Dispatch<React.SetStateAction<boolean>>;
  logoutFunc: () => void;
  isLinkAcc: boolean;
  isAccountRestricted: () => boolean;
  assets: { we_are_sorry?: string };
  derogatoryData: {
    rbMoreDerogatoryStatusTitle?: string;
    rbMoreDerogatoryStatusMessage?: string;
    rbMoreBtnOk?: string;
  };
  isEppVisible: boolean;
  isPasskeyRegistered?: boolean;
  setUnlinkAccountPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

type UserStateSlice = {
  user: {
    accountDetails?: {
      productName?: string;
      customer?: {
        firstName?: string;
        lastName?: string;
      };
    };
  };
};

type ContentfulStateSlice = {
  contentful: {
    contentfulData?: {
      entries?: {
        rb_offers?: {
          rbOffersFlag?: {
            HideViewAllOffersWeb?: boolean;
          };
        };
      };
    };
  };
};

const MobileSideMenu = ({
  contentfulData,
  isMenuOpenValue,
  setIsMobileMenuOpen,
  featureFlaggingData,
  setShowFeatureFlagMessage,
  logoutFunc,
  isLinkAcc,
  isAccountRestricted,
  assets,
  derogatoryData,
  isEppVisible,
  isPasskeyRegistered,
  setUnlinkAccountPopup,
}: Props) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [activeMenuItem, setActiveMenuItem] = useState('Home');
  const [isMenuOpen, setIsMenuOpen] = useState(isMenuOpenValue);

  const [lang, setLang] = useState(localStorage.getItem('language') || 'en-US');
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [activeMoreItem, setActiveMoreItem] = useState(false);
  const [isFaqsModalVisible, setIsFaqsModalVisible] = useState(false);
  const [isLegalPrivacyModalVisible, setIsLegalPrivacyModalVisible] =
    useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isContactUsOpened, setIsContactUsOpened] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);

  const menuToggleBtnRef = useRef<HTMLButtonElement>(null);
  const menuCloseBtnRef = useRef<HTMLButtonElement>(null);
  const moreMenus = contentfulData?.rb_navigation?.moreMenu;
  const managePasskeyBlocked =
    featureFlaggingData?.more?.manage_passkey === false ||
    !resolvePasskeyFeatureFlags(featureFlaggingData).web.deletionEnabled;

  const { accountDetails } = useSelector((state: UserStateSlice) => state.user);
  const { contentfulData: contentful } = useSelector(
    (state: ContentfulStateSlice) => state.contentful
  );
  const { entries } = contentful || {};
  const { rb_offers } = entries || {};

  const handleLanguageChange = (selectedLang: string) => {
    setLang(selectedLang);
    localStorage.setItem('language', selectedLang);
    setIsLanguageMenuOpen(false);
    navigate(0);
  };
  const trackEventMenu = (name: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      globalThis.location?.href ?? '',
      `dashboard|navigation|${name}`
    );
  };

  useEffect(() => {
    if (isMenuOpen) {
      menuCloseBtnRef.current?.focus();
      document.body.classList.add('overflow-hidden');
    } else {
      menuToggleBtnRef.current?.focus();
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isMenuOpen]);

  const handleMenuClick = (menuItem: string, path: string) => {
    setActiveMenuItem(menuItem);
    trackEventMenu(menuItem);
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleFeatureFlag = () => {
    setIsMobileMenuOpen(!isMenuOpenValue);
    setShowFeatureFlagMessage(true);
    setTimeout(() => {
      setShowFeatureFlagMessage(false);
    }, 3000);
  };

  const handleEnablePushNotifications = async () => {
    if (typeof Notification === 'undefined') {
      setShowNotificationPopup(true);
      return;
    }

    if (Notification.permission === 'granted') {
      setIsMenuOpen(false);
      return;
    }

    if (Notification.permission === 'default') {
      let didRequestPermission = false;

      try {
        const { requestBrazePushPermission } =
          await import('../../../config/braze');
        didRequestPermission = requestBrazePushPermission();
      } catch (error) {
        console.error('[Braze] failed to request web push permission:', error);
      }

      const permissionAfterRequest = globalThis.Notification.permission;

      if (!didRequestPermission && permissionAfterRequest === 'denied') {
        setShowNotificationPopup(true);
      }

      setIsMenuOpen(false);
      return;
    }

    setShowNotificationPopup(true);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logoutFunc();
    setIsMobileMenuOpen(!isMenuOpenValue);
    if (localStorage.getItem('language')) {
      navigate(`/sign-in?locale=${localStorage.getItem('language')}`);
    } else {
      navigate('/sign-in');
    }
  };
  const productName = accountDetails?.productName;
  const isOffersEnabledForProduct = Boolean(
    productName
      ? featureFlaggingData?.navigation
          ?.offersFeatureFlaggedBasedOnProductType?.[productName]
      : false
  );
  const shouldShowEnablePushNotifications =
    typeof Notification === 'undefined' ||
    Notification.permission !== 'granted';

  return (
    <>
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black opacity-50 z-[9998]"></div>
      )}
      <aside
        className={`block lg:hidden md:hidden bg-white text-black w-[88%] lg:w-16 fixed lg:static top-0 left-0 h-full z-[9999] shadow-md transform ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-300 ease-in-out`}
        aria-label="Sidebar Navigation"
      >
        <nav
          aria-label="Main Menu"
          className="mt-2 lg:mt-0 lg:flex lg:flex-col lg:items-center p-0 h-full overflow-y-auto max-h-screen"
          role="menu"
        >
          <div className="flex bg-gray-50 justify-between p-4 cursor-pointer">
            <button
              onClick={() => setIsMobileMenuOpen(!isMenuOpenValue)}
              aria-label="Toggle Menu"
              aria-expanded={isMenuOpenValue}
              aria-controls="mobile-menu"
              ref={menuToggleBtnRef}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={
                    isMenuOpenValue
                      ? 'M6 18L18 6M6 6l12 12'
                      : 'M4 6h16M4 12h16m-7 6h7'
                  }
                ></path>
              </svg>
            </button>
            <span
              className="text-lg font-[500] lg:text-center pr-[8%] cursor-pointer"
              onClick={handleLogout}
            >
              {contentfulData?.rb_header?.signOutCtaTitle}
            </span>
          </div>
          <div className="flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300">
            <span className="text-lg font-[500] lg:text-center">
              Hello, {accountDetails?.customer?.firstName ?? 'Rogers'}{' '}
              {accountDetails?.customer?.lastName ?? 'User'}
            </span>
          </div>
          {/* //home */}
          <button
            type="button"
            className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300 w-full`}
            aria-label={contentfulData?.rb_navigation?.homeLogoTitle}
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              handleMenuClick('Home', '/home');
            }}
            onKeyDown={(e) =>
              e.key === 'Enter' && handleMenuClick('Home', '/home')
            }
            aria-current={activeMenuItem === 'Home' ? 'page' : undefined}
          >
            <HomeIcon fill="#000" />
            <span className="text-lg font-[500] lg:text-center">
              {contentfulData?.rb_navigation?.homeLogoTitle}
            </span>
          </button>
          {/* //home */}
          {/* //offer !rb_offers?.rbOffersFlag?.HideViewAllOffersWeb */}

          {!rb_offers?.rbOffersFlag?.HideViewAllOffersWeb && (
            <button
              type="button"
              className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300 w-full`}
              aria-label={contentfulData?.rb_navigation?.offersLogoTitle}
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                if (isOffersEnabledForProduct) {
                  handleMenuClick('Offers', '/offers');
                } else {
                  handleFeatureFlag();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (isOffersEnabledForProduct) {
                    handleMenuClick('Offers', '/offers');
                  } else {
                    handleFeatureFlag();
                  }
                }
              }}
              aria-current={activeMenuItem === 'Offers' ? 'page' : undefined}
            >
              <OfferIcon
                fill={featureFlaggingData?.navigation?.offers ? '#000' : '#ccc'}
              />
              <span
                className={`${featureFlaggingData?.navigation?.offers ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
              >
                {contentfulData?.rb_navigation?.offersLogoTitle}
              </span>
            </button>
          )}
          {/* //offer */}
          {/* ManageCard */}
          <button
            type="button"
            className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300 w-full`}
            aria-label={contentfulData?.rb_navigation?.manageCardLogoTitle}
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              handleMenuClick('ManageCard', '/manage-card');
            }}
            onKeyDown={(e) =>
              e.key === 'Enter' && handleMenuClick('ManageCard', '/manage-card')
            }
            aria-current={activeMenuItem === 'ManageCard' ? 'page' : undefined}
          >
            <ManagedCard fill="#000" />
            <span className="text-lg font-[500] lg:text-center">
              {contentfulData?.rb_navigation?.manageCardLogoTitle}
            </span>
          </button>
          {/* ManageCard */}
          {/* {Rewards} */}
          <button
            type="button"
            className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300 w-full`}
            aria-label={contentfulData?.rb_navigation?.rewardsLogoTitle}
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              handleMenuClick('Rewards', '/rewards');
            }}
            onKeyDown={(e) =>
              e.key === 'Enter' && handleMenuClick('Rewards', '/rewards')
            }
            aria-current={activeMenuItem === 'Rewards' ? 'page' : undefined}
          >
            <RewardIcon fill="#000" />
            <span className="text-lg font-[500] lg:text-center">
              {contentfulData?.rb_navigation?.rewardsLogoTitle}
            </span>
          </button>
          {isEppVisible && (
            <p
              role="menuitem"
              className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                handleMenuClick('More', '/equal-payment-plan');
              }}
              aria-label="Select View EPP"
            >
              <RespViewEPPIcon fill="#000" />
              <span className="text-lg font-[500] lg:text-center">
                {moreMenus?.view_epp}
              </span>
            </p>
          )}

          {/* //More */}
          <div className="relative">
            <button
              type="button"
              className="flex justify-between items-center border-b border-gray-300 w-full"
              onClick={() => {
                if (!activeMoreItem) {
                  logBrazeCustomEvent('viewed_more_mobile');
                }
                setActiveMoreItem(!activeMoreItem);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!activeMoreItem) {
                    logBrazeCustomEvent('viewed_more_mobile');
                  }
                  setActiveMoreItem(!activeMoreItem);
                }
              }}
              aria-haspopup="true"
              aria-expanded={activeMoreItem}
              aria-controls="more-options"
              aria-label={`More option selection`}
              tabIndex={0}
            >
              <span className="flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2">
                <MoreIcon fill="#000" />
                <span className="text-lg font-[500] lg:text-center">
                  {contentfulData?.rb_navigation?.moreLogoTitle}
                </span>
              </span>
              <span className="pr-[8%]" aria-hidden="true">
                {activeMoreItem ? <CloseIcon /> : <DownArrow />}
              </span>
            </button>

            {activeMoreItem && (
              <div
                id="more-options"
                role="menu"
                aria-labelledby="more-selection"
                className="relative z-[9999] bg-white border border-gray-300 text-lg font-[500] lg:text-center left-0 w-[100%]"
              >
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isAccountRestricted()) {
                      setShowPopup(true);
                    } else {
                      handleMenuClick('More', '/manage-alerts');
                    }
                  }}
                  aria-label="Select Manage alerts"
                >
                  <RespManageAlertsIcon fill="#000" />
                  <span
                    className={`${featureFlaggingData?.more?.manage_alerts ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.manage_alerts}
                  </span>
                </p>
                {shouldShowEnablePushNotifications && (
                  <button
                    className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      handleEnablePushNotifications();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnablePushNotifications();
                      }
                    }}
                    aria-label="Select Enable Push Notifications"
                  >
                    <RespManageAlertsIcon
                      fill={
                        (featureFlaggingData?.webMore
                          ?.enable_push_notifications ??
                          featureFlaggingData?.webMore?.notifications) === false
                          ? '#ccc'
                          : '#000'
                      }
                    />
                    <span
                      className={`${
                        (featureFlaggingData?.webMore
                          ?.enable_push_notifications ??
                          featureFlaggingData?.webMore?.notifications) === false
                          ? 'text-gray-300'
                          : 'text-black'
                      } text-lg font-[500] lg:text-center`}
                    >
                      {moreMenus?.enable_push_notifications ||
                        'Enable Push Notifications'}
                    </span>
                  </button>
                )}
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsContactUsOpened(true);
                    setIsMenuOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setIsContactUsOpened(true);
                      setIsMenuOpen(false);
                    }
                  }}
                  aria-label="Select Contact us"
                >
                  <RespContactUsIcon
                    fill={
                      featureFlaggingData?.more?.contact_us ? '#000' : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.more?.contact_us ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.contact_us}
                  </span>
                </p>
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick('PreAuthDebit', '/pre-authorized-debit');
                  }}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    handleMenuClick('PreAuthDebit', '/pre-authorized-debit')
                  }
                  aria-label="Select Pre-authorized debit"
                >
                  <RespPreauthorizedDebitIcon
                    fill={
                      featureFlaggingData?.more?.pre_authorized_debit
                        ? '#000'
                        : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.more?.pre_authorized_debit ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.pre_autorised_debit}
                  </span>
                </p>
                {isPasskeyRegistered && (
                  <p
                    role="menuitem"
                    className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      if (managePasskeyBlocked) {
                        handleFeatureFlag();
                      } else {
                        handleMenuClick('More', '/manage-passkey');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (managePasskeyBlocked) {
                          handleFeatureFlag();
                        } else {
                          handleMenuClick('More', '/manage-passkey');
                        }
                      }
                    }}
                    aria-label="Select Manage Passkeys"
                  >
                    <RespManagePasskeyIcon
                      fill={managePasskeyBlocked ? '#ccc' : '#000'}
                    />
                    <span
                      className={`${managePasskeyBlocked ? 'text-gray-300' : 'text-black'} text-lg font-[500] lg:text-center`}
                    >
                      {moreMenus?.manage_passkey}
                    </span>
                  </p>
                )}
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    if (featureFlaggingData?.navigation?.cardFeature) {
                      handleFeatureFlag();
                    } else {
                      handleMenuClick('More', '/card-features');
                    }
                  }}
                  aria-label="Select Card features"
                >
                  <RespCardFeaturesIcon
                    fill={
                      featureFlaggingData?.more?.card_features ? '#000' : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.more?.card_features ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.card_feature}
                  </span>
                </p>
                {/* //secure message */}
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    handleMenuClick('More', '/secure-messages');
                    // handleFeatureFlag()
                  }}
                  aria-label="view and send messages"
                >
                  <RespSendAndViewRequestIcon
                    fill={
                      featureFlaggingData?.more?.send_and_view_msgs
                        ? '#000'
                        : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.more?.send_and_view_msgs ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.send_and_view_msgs}
                  </span>
                </p>
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsFaqsModalVisible(true);
                    setIsMenuOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setIsFaqsModalVisible(true);
                      setIsMenuOpen(false);
                    }
                  }}
                  aria-label="Select FAQs"
                >
                  <RespFAQsIcon
                    fill={featureFlaggingData?.more?.faq ? '#000' : '#ccc'}
                  />
                  <span
                    className={`${featureFlaggingData?.more?.faq ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.faqs}
                  </span>
                </p>
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    if (featureFlaggingData?.webMore?.legal_privacy === false) {
                      handleFeatureFlag();
                    } else {
                      setIsLegalPrivacyModalVisible(true);
                      setIsMenuOpen(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (
                        featureFlaggingData?.webMore?.legal_privacy === false
                      ) {
                        handleFeatureFlag();
                      } else {
                        setIsLegalPrivacyModalVisible(true);
                        setIsMenuOpen(false);
                      }
                    }
                  }}
                  aria-label="Select Legal Privacy"
                >
                  <RespLegalPrivacyIcon
                    fill={
                      featureFlaggingData?.webMore?.legal_privacy
                        ? '#000'
                        : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.webMore?.legal_privacy ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.legal_privacy}
                  </span>
                </p>
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={(e) => {
                    handleMenuClick('More', '/service-request');
                    // handleFeatureFlag()
                  }}
                  aria-label="Select view service request"
                >
                  <RespViewServiceReqIcon
                    fill={
                      featureFlaggingData?.more?.view_service_request
                        ? '#000'
                        : '#ccc'
                    }
                  />
                  <span
                    className={`${featureFlaggingData?.more?.view_service_request ? 'text-black' : 'text-gray-300'} text-lg font-[500] lg:text-center`}
                  >
                    {moreMenus?.view_service_req}
                  </span>
                </p>
                {isLinkAcc && (
                  <p
                    role="menuitem"
                    className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      setUnlinkAccountPopup(true);
                      setIsMenuOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setUnlinkAccountPopup(true);
                        setIsMenuOpen(false);
                      }
                    }}
                    aria-label="Select Unlink us"
                  >
                    <RespUnlinkRogersAcc fill={'#000'} />
                    <span className="text-black text-lg font-[500] lg:text-center">
                      {moreMenus?.unLink_rogers_acc}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center border-b border-gray-300">
            <button
              type="button"
              className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 w-full`}
              aria-label={contentfulData?.rb_header?.myAccountTitle}
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                handleMenuClick('MyProfile', '/my-profile');
              }}
              onKeyDown={(e) =>
                e.key === 'Enter' && handleMenuClick('MyProfile', '/my-profile')
              }
              aria-current={activeMenuItem === 'MyProfile' ? 'page' : undefined}
            >
              <UserICon fill={'#000'} />
              <span className={'text-black text-lg font-[500] lg:text-center'}>
                {moreMenus?.my_profile}
              </span>
            </button>
          </div>
          <div className="relative">
            <button
              type="button"
              className="flex justify-between items-center border-b border-gray-300 w-full"
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsLanguageMenuOpen(!isLanguageMenuOpen);
                }
              }}
              aria-haspopup="true"
              aria-expanded={isLanguageMenuOpen}
              aria-controls="language-options"
              aria-label={`Language selection. Currently selected: ${lang}`}
              tabIndex={0}
            >
              <span className="flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2">
                <LanguageIcon aria-hidden="true" />
                <span className="text-lg font-[500] lg:text-center">
                  {lang == 'en-US'
                    ? contentfulData?.rb_header?.englishTitle
                    : contentfulData?.rb_header?.frenchTitle}
                </span>
              </span>
              <span className="pr-[8%]" aria-hidden="true">
                {isLanguageMenuOpen ? <CloseIcon /> : <DownArrow />}
              </span>
            </button>

            {isLanguageMenuOpen && (
              <div
                id="language-options"
                role="menu"
                aria-labelledby="language-selection"
                className="relative bg-white border border-gray-300 text-lg font-[500] lg:text-center left-0 w-[100%]"
              >
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={() => handleLanguageChange('en-US')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLanguageChange('en-US');
                    }
                  }}
                  aria-label="Select English as your language"
                >
                  {contentfulData?.rb_header?.englishTitle || 'English'}
                </p>
                <p
                  role="menuitem"
                  className={`flex items-center space-x-3 p-4 lg:flex-col lg:space-y-1 lg:space-x-0 lg:p-2 border-b border-gray-300`}
                  tabIndex={0}
                  onClick={() => handleLanguageChange('fr')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLanguageChange('fr');
                    }
                  }}
                  aria-label="Select French as your language"
                >
                  {contentfulData?.rb_header?.frenchTitle || 'French'}
                </p>
              </div>
            )}
          </div>
        </nav>
      </aside>
      {isFaqsModalVisible && (
        <FAQsModal
          isFAQsModalOpen={isFaqsModalVisible}
          setIsFAQsModalOpen={setIsFaqsModalVisible}
        />
      )}
      {isLegalPrivacyModalVisible && (
        <LegalPrivacyModal
          isLegalPrivacyModalOpen={isLegalPrivacyModalVisible}
          setIsLegalPrivacyModalOpen={setIsLegalPrivacyModalVisible}
        />
      )}
      {showPopup && (
        <Modal
          isOpen={showPopup}
          contentLabel="derogatoryPopup"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.we_are_sorry}
              alt="sorryIcon"
              className="w-20 h-20"
            />
            <h3 className="text-[#1F1F1F] text-[18px] font-[500] leading-[25px] tracking-[0.068px] text-center">
              {derogatoryData.rbMoreDerogatoryStatusTitle}
            </h3>
            <p className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center">
              {derogatoryData.rbMoreDerogatoryStatusMessage}
            </p>
            <button
              type="button"
              onClick={() => {
                setShowPopup(false);
              }}
              className="text-center flex-1 items-center align-center text-white px-[20px] py-[12px] bg-primary-red w-full"
            >
              {derogatoryData.rbMoreBtnOk}
            </button>
          </div>
        </Modal>
      )}
      {isContactUsOpened && (
        <ContactUs
          isContactUsOpened={isContactUsOpened}
          setIsContactUsOpened={setIsContactUsOpened}
        />
      )}
      {showNotificationPopup && (
        <CommanModal
          isOpen={showNotificationPopup}
          onClose={() => setShowNotificationPopup(false)}
          closeIcon={false}
          buttonText={
            contentfulData?.rb_navigation?.rbNotifications
              ?.enable_notification_popup_primary_button
          }
          jsxContent={
            <p className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center">
              {
                contentfulData?.rb_navigation?.rbNotifications
                  ?.enable_notification_popup_title
              }
            </p>
          }
        />
      )}
    </>
  );
};

export default MobileSideMenu;
